"use client";

import { useCallback, useEffect, useRef, useState } from "react";

/** The offerings section's "0 to 120 days" line, driven by a video.
 *
 *  The design sets that line as the largest type on the page after the
 *  section headings. Here the leading figure counts up from zero to the
 *  number the copy carries while the video plays, so the sentence and the
 *  footage finish together and the line ends up reading exactly as written.
 *
 *  Two sources, one behaviour: a YouTube URL becomes an embed driven through
 *  the IFrame API, anything else is a plain <video>. Either way the count is
 *  read off the player's current time on an animation frame rather than off
 *  progress events, which fire a few times a second — fast enough for a
 *  progress bar, visibly steppy on a number this large.
 *
 *  Playback starts when the section scrolls into view, not on load: a video
 *  that autoplays at the top of a long page has finished counting long before
 *  anyone reaches it. Autoplay is muted because every browser requires it.
 */

/** 11-character id out of any of YouTube's URL shapes. */
export function youTubeId(src: string): string | null {
  const m = src.match(
    /(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/,
  );
  return m ? m[1] : null;
}

type Player = {
  getCurrentTime: () => number;
  getDuration: () => number;
  playVideo: () => void;
  destroy: () => void;
};

/** The IFrame API script is global and single-load; every player waits on the
 *  same promise rather than each racing its own <script> tag. */
let ytReady: Promise<void> | null = null;
function loadYouTubeApi(): Promise<void> {
  if (ytReady) return ytReady;
  ytReady = new Promise<void>((resolve) => {
    const w = window as unknown as { YT?: { Player?: unknown }; onYouTubeIframeAPIReady?: () => void };
    if (w.YT?.Player) return resolve();
    const prev = w.onYouTubeIframeAPIReady;
    w.onYouTubeIframeAPIReady = () => {
      prev?.();
      resolve();
    };
    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    document.head.appendChild(tag);
  });
  return ytReady;
}

export function DaysCountVideo({
  src,
  poster,
  badge,
  label,
  className = "",
}: {
  src: string;
  poster?: string;
  /** The full line, e.g. "0 to 120 days". The first number is animated and
   *  the second is read as the target. Rendered verbatim if it holds fewer
   *  than two numbers. */
  badge: string;
  label: string;
  /** Caps the video's width; the count line stays full width beneath it. */
  className?: string;
}) {
  // \D+ between the two figures, not a lazy any-run: with [\s\S]*? a line
  // holding a single number ("120 days") would split it into "1" and "20"
  // and animate the fragment.
  const parsed = badge.match(/^(\s*)(\d+)(\D+)(\d+)([\s\S]*)$/);
  // The FIRST figure is the destination: it counts up from zero to the number
  // the copy actually carries, and everything after it — including the second
  // figure — stays put. So "90 to 120 days" opens on "0 to 120 days" and
  // settles on the line as written.
  //
  // It used to count to the SECOND figure instead, which ended on "120 to 120
  // days" — a sentence that no longer describes a range. That only looked
  // right while the copy read "0 to 120", where the number being animated
  // started at zero anyway.
  const target = parsed ? Number(parsed[2]) : 0;
  // The effect must not depend on `parsed` itself — a fresh array every
  // render would re-run it on every state change it causes, forever.
  const hasCount = parsed !== null;
  const ytId = youTubeId(src);

  const videoRef = useRef<HTMLVideoElement>(null);
  const holderRef = useRef<HTMLDivElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<number | null>(null);
  const playerRef = useRef<Player | null>(null);
  const startedRef = useRef(false);
  const [count, setCount] = useState(0);
  // Until the client takes over — and whenever motion is unwelcome or the
  // clip will not play — the line reads exactly as the copy is written, i.e.
  // starting at its own first figure.
  const [live, setLive] = useState(false);

  const stopLoop = useCallback(() => {
    if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    frameRef.current = null;
  }, []);

  /** One source of truth for the count, whichever player is behind it. */
  const tick = useCallback(() => {
    const read = playerRef.current
      ? { t: playerRef.current.getCurrentTime(), d: playerRef.current.getDuration() }
      : videoRef.current
        ? { t: videoRef.current.currentTime, d: videoRef.current.duration }
        : null;
    if (read && Number.isFinite(read.d) && read.d > 0) {
      setCount(Math.round(Math.min(1, read.t / read.d) * target));
    }
    frameRef.current = requestAnimationFrame(tick);
  }, [target]);

  useEffect(() => {
    if (!hasCount) return;
    const wrap = wrapRef.current;
    if (!wrap) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let cancelled = false;
    setLive(true);
    setCount(0);

    const startPlain = () => {
      const v = videoRef.current;
      if (!v) return;
      // Belt and braces: an unmuted video is refused without a user gesture,
      // and React has been unreliable about reflecting the `muted` prop.
      v.muted = true;
      v.play().then(
        () => {
          startedRef.current = true;
          tick();
        },
        () => setLive(false),
      );
    };

    const startYouTube = async () => {
      await loadYouTubeApi();
      if (cancelled || !holderRef.current) return;
      const YT = (window as unknown as { YT: { Player: new (el: HTMLElement, o: unknown) => Player } }).YT;
      playerRef.current = new YT.Player(holderRef.current, {
        videoId: ytId!,
        playerVars: {
          autoplay: 1, mute: 1, controls: 1, rel: 0, modestbranding: 1,
          playsinline: 1, origin: window.location.origin,
        },
        events: {
          onReady: (e: { target: Player }) => {
            if (cancelled) return;
            startedRef.current = true;
            e.target.playVideo();
            tick();
          },
          // 0 = ENDED. Pin the figure rather than leaving it a frame short.
          onStateChange: (e: { data: number }) => {
            if (e.data === 0) {
              stopLoop();
              setCount(target);
            }
          },
          onError: () => setLive(false),
        },
      });
    };

    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        io.disconnect();
        if (ytId) void startYouTube();
        else startPlain();
      },
      { threshold: 0.4 },
    );
    io.observe(wrap);

    const v = videoRef.current;
    const onEnded = () => {
      stopLoop();
      setCount(target);
    };
    v?.addEventListener("ended", onEnded);

    // Browsers pause background video, which would strand the count at
    // whatever number the clip had reached. Pick it back up on return.
    const onVisibility = () => {
      if (document.visibilityState !== "visible" || !startedRef.current) return;
      if (playerRef.current) playerRef.current.playVideo();
      else if (v && !v.ended) void v.play().catch(() => {});
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      io.disconnect();
      v?.removeEventListener("ended", onEnded);
      document.removeEventListener("visibilitychange", onVisibility);
      stopLoop();
      playerRef.current?.destroy();
      playerRef.current = null;
    };
  }, [hasCount, stopLoop, target, tick, ytId]);

  return (
    <div ref={wrapRef} className="mt-14">
      {ytId ? (
        // The API replaces this div with its iframe, so the aspect box is the
        // parent — a ratio on the div itself would go with it.
        <div className={`aspect-video w-full overflow-hidden rounded-[10px] ${className}`}>
          <div ref={holderRef} className="h-full w-full" />
        </div>
      ) : (
        <video
          ref={videoRef}
          src={src}
          poster={poster}
          muted
          playsInline
          preload="metadata"
          aria-label={label}
          className={`aspect-video w-full rounded-[10px] object-cover ${className}`}
        />
      )}
      <p className="mt-10 text-center text-[clamp(2.25rem,5.76vw,5.1875rem)] font-medium leading-[1.13] text-black">
        {parsed ? (
          <>
            {parsed[1]}
            <span className="tabular-nums">{live ? count : parsed[2]}</span>
            {parsed[3]}
            {parsed[4]}
            {parsed[5]}
          </>
        ) : (
          badge
        )}
      </p>
    </div>
  );
}
